package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.local.preferences.TokenStorage
import com.iptvplayer.shared.data.remote.api.AuthApi
import com.iptvplayer.shared.data.remote.dto.LoginRequestDto
import com.iptvplayer.shared.data.remote.dto.RefreshRequestDto
import com.iptvplayer.shared.data.remote.dto.RegisterRequestDto
import com.iptvplayer.shared.domain.model.AuthResponse
import com.iptvplayer.shared.domain.model.AuthToken
import com.iptvplayer.shared.domain.model.User
import com.iptvplayer.shared.domain.repository.AuthRepository
import com.iptvplayer.shared.util.Result

class AuthRepositoryImpl(
    private val authApi: AuthApi,
    private val tokenStorage: TokenStorage
) : AuthRepository {

    override suspend fun register(
        username: String,
        email: String,
        password: String
    ): Result<AuthResponse> = try {
        val response = authApi.register(RegisterRequestDto(username, email, password))
        val user = User(
            id = response.user.id,
            username = response.user.username,
            email = response.user.email,
            createdAt = response.user.createdAt,
            isAdmin = response.user.isAdmin
        )
        val token = AuthToken(
            accessToken = response.tokens.accessToken,
            refreshToken = response.tokens.refreshToken,
            expiresAt = response.tokens.expiresAt
        )
        tokenStorage.saveToken(token)
        tokenStorage.saveUser(user)
        Result.Success(AuthResponse(user, token))
    } catch (e: Exception) {
        Result.Error("Registration failed: ${e.message}", e)
    }

    override suspend fun login(username: String, password: String): Result<AuthResponse> = try {
        val response = authApi.login(LoginRequestDto(username, password))
        val user = User(
            id = response.user.id,
            username = response.user.username,
            email = response.user.email,
            createdAt = response.user.createdAt,
            isAdmin = response.user.isAdmin
        )
        val token = AuthToken(
            accessToken = response.tokens.accessToken,
            refreshToken = response.tokens.refreshToken,
            expiresAt = response.tokens.expiresAt
        )
        tokenStorage.saveToken(token)
        tokenStorage.saveUser(user)
        Result.Success(AuthResponse(user, token))
    } catch (e: Exception) {
        Result.Error("Login failed: ${e.message}", e)
    }

    override suspend fun refreshToken(refreshToken: String): Result<AuthToken> = try {
        val response = authApi.refreshToken(RefreshRequestDto(refreshToken))
        val token = AuthToken(
            accessToken = response.accessToken,
            refreshToken = response.refreshToken,
            expiresAt = response.expiresAt
        )
        tokenStorage.saveToken(token)
        Result.Success(token)
    } catch (e: Exception) {
        Result.Error("Token refresh failed: ${e.message}", e)
    }

    override suspend fun logout(): Result<Unit> = try {
        authApi.logout()
        tokenStorage.clear()
        Result.Success(Unit)
    } catch (e: Exception) {
        // Clear local session even if server call fails
        tokenStorage.clear()
        Result.Error("Logout failed: ${e.message}", e)
    }

    override suspend fun requestAccountDeletion(): Result<Unit> = try {
        // Flags the account for deletion; an admin reviews and performs the
        // actual purge. The local session stays valid until then.
        authApi.requestAccountDeletion()
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Deletion request failed: ${e.message}", e)
    }

    override suspend fun getCurrentUser(): User? = tokenStorage.getUser()

    override suspend fun getStoredToken(): AuthToken? = tokenStorage.getToken()

    override suspend fun saveToken(token: AuthToken) {
        tokenStorage.saveToken(token)
    }

    override suspend fun saveUser(user: User) {
        tokenStorage.saveUser(user)
    }

    override suspend fun clearSession() {
        tokenStorage.clear()
    }

    override fun isLoggedIn(): Boolean = tokenStorage.getToken() != null
}
